import { lazy, Suspense, type ReactNode } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { ThemeProvider, CssBaseline, Box, CircularProgress } from '@mui/material'
import { Provider } from 'react-redux'
import { store } from './store'
import Layout from './components/Layout'
import ProtectedRoute from './components/ProtectedRoute'
import CourtroomAccessGuard from './components/CourtroomAccessGuard'
import StudentStageGuard from './components/StudentStageGuard'
import ForbiddenToast from './components/ForbiddenToast'
import LoginPage from './pages/LoginPage'
import { appTheme } from './theme/appTheme'

const HomePage = lazy(() => import('./pages/HomePage'))
const TeamsPage = lazy(() => import('./pages/TeamsPage'))
const CasesPage = lazy(() => import('./pages/CasesPage'))
const MemorialsPage = lazy(() => import('./pages/MemorialsPage'))
const EvidencePage = lazy(() => import('./pages/EvidencePage'))
const R1SubmissionPage = lazy(() => import('./pages/R1SubmissionPage'))
const DossierPage = lazy(() => import('./pages/DossierPage'))
const RoleSheetsPage = lazy(() => import('./pages/RoleSheetsPage'))
const ScoringPage = lazy(() => import('./pages/ScoringPage'))
const ProfilePage = lazy(() => import('./pages/ProfilePage'))
const UsersPage = lazy(() => import('./pages/UsersPage'))
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'))
const CourtroomPage = lazy(() => import('./pages/CourtroomPage'))
const ReportsPage = lazy(() => import('./pages/ReportsPage'))
const JudgeAssignmentsPage = lazy(() => import('./pages/JudgeAssignmentsPage'))
const BracketsPage = lazy(() => import('./pages/BracketsPage'))
const AchievementsPage = lazy(() => import('./pages/AchievementsPage'))
const VerdictsReviewPage = lazy(() => import('./pages/VerdictsReviewPage'))
const ExamInfoPage = lazy(() => import('./pages/ExamInfoPage'))
const PublicResultsPage = lazy(() => import('./pages/PublicResultsPage'))
const PublicLandingPage = lazy(() => import('./pages/PublicLandingPage'))
const PublicCourtroomWatchPage = lazy(() => import('./pages/PublicCourtroomWatchPage'))
const IntroVideoPage = lazy(() => import('./pages/IntroVideoPage'))
const SecretaryDeskPage = lazy(() => import('./pages/SecretaryDeskPage'))

/** Tải chunk route — hiển thị khi lazy import đang fetch */
function RouteFallback() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
      <CircularProgress color="primary" aria-label="Đang tải trang" />
    </Box>
  )
}

function withSuspense(node: ReactNode) {
  return <Suspense fallback={<RouteFallback />}>{node}</Suspense>
}
// DocumentsPage và RAGSearchPage đã được tích hợp vào CasesPage

function App() {
  return (
    <Provider store={store}>
      <ThemeProvider theme={appTheme}>
        <CssBaseline />
        <ForbiddenToast />
        <Box sx={{ 
          minHeight: '100dvh',
          width: '100%',
          margin: 0,
          padding: 0,
          bgcolor: 'background.default',
          overflowX: 'hidden',
          overflowY: 'auto',
        }}>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            {/* Public Guest routes — không cần đăng nhập */}
            <Route path="/" element={withSuspense(<PublicLandingPage />)} />
            <Route path="/public/results" element={withSuspense(<PublicResultsPage />)} />
            <Route path="/public/results/:caseId" element={withSuspense(<PublicResultsPage />)} />
            <Route path="/public/courtroom/:courtroomId" element={withSuspense(<PublicCourtroomWatchPage />)} />
            <Route element={<Layout />}>
              <Route path="/dashboard" element={<ProtectedRoute>{withSuspense(<HomePage />)}</ProtectedRoute>} />
              <Route path="/teams" element={<ProtectedRoute>{withSuspense(<TeamsPage />)}</ProtectedRoute>} />
              <Route path="/cases" element={<ProtectedRoute>{withSuspense(<CasesPage />)}</ProtectedRoute>} />
              <Route path="/memorials" element={
                <ProtectedRoute>
                  <StudentStageGuard required="r1" featureName="Hồ sơ Memorial">{withSuspense(<MemorialsPage />)}</StudentStageGuard>
                </ProtectedRoute>
              } />
              <Route path="/r1-submission" element={
                <ProtectedRoute>
                  <StudentStageGuard required="r1" featureName="Nộp bài Vòng Sơ loại">{withSuspense(<R1SubmissionPage />)}</StudentStageGuard>
                </ProtectedRoute>
              } />
              <Route path="/evidence" element={
                <ProtectedRoute>
                  <StudentStageGuard required="r1" featureName="Chứng cứ">{withSuspense(<EvidencePage />)}</StudentStageGuard>
                </ProtectedRoute>
              } />
              <Route path="/dossier" element={
                <ProtectedRoute>
                  <StudentStageGuard required="r1" featureName="Hồ sơ khởi kiện">{withSuspense(<DossierPage />)}</StudentStageGuard>
                </ProtectedRoute>
              } />
              <Route path="/role-sheets" element={
                <ProtectedRoute>
                  <StudentStageGuard required="r2" featureName="Bảng phân vai">{withSuspense(<RoleSheetsPage />)}</StudentStageGuard>
                </ProtectedRoute>
              } />
              <Route path="/intro-video" element={
                <ProtectedRoute>
                  <StudentStageGuard required="r1" featureName="Video giới thiệu">{withSuspense(<IntroVideoPage />)}</StudentStageGuard>
                </ProtectedRoute>
              } />
              <Route
                path="/scoring"
                element={
                  <ProtectedRoute allowedRoles={['judge', 'secretary']}>
                    {withSuspense(<ScoringPage />)}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/judge-assignments"
                element={
                  <ProtectedRoute allowedRoles={['secretary', 'support', 'judge']}>
                    {withSuspense(<JudgeAssignmentsPage />)}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/courtroom"
                element={
                  <ProtectedRoute>
                    <CourtroomAccessGuard>{withSuspense(<CourtroomPage />)}</CourtroomAccessGuard>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reports"
                element={
                  <ProtectedRoute allowedRoles={['secretary', 'support']}>
                    {withSuspense(<ReportsPage />)}
                  </ProtectedRoute>
                }
              />
              <Route path="/brackets" element={<ProtectedRoute>{withSuspense(<BracketsPage />)}</ProtectedRoute>} />
              <Route
                path="/notifications"
                element={<ProtectedRoute>{withSuspense(<NotificationsPage />)}</ProtectedRoute>}
              />
              <Route path="/profile" element={<ProtectedRoute>{withSuspense(<ProfilePage />)}</ProtectedRoute>} />
              <Route
                path="/users"
                element={
                  <ProtectedRoute allowedRoles={['secretary', 'support']}>
                    {withSuspense(<UsersPage />)}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/achievements"
                element={
                  // 2026-08-21 (Legal request): chặn student truy cập cert +
                  // rank chi tiết. Chỉ BTC (secretary/support/admin) + judge.
                  <ProtectedRoute allowedRoles={['secretary', 'support', 'admin', 'judge', 'judge_president']}>
                    {withSuspense(<AchievementsPage />)}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/verdicts-review"
                element={
                  <ProtectedRoute allowedRoles={['secretary', 'support']}>
                    {withSuspense(<VerdictsReviewPage />)}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/secretary-desk"
                element={
                  <ProtectedRoute allowedRoles={['secretary']}>
                    {withSuspense(<SecretaryDeskPage />)}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cases/:caseId/exam"
                element={<ProtectedRoute>{withSuspense(<ExamInfoPage />)}</ProtectedRoute>}
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
        </BrowserRouter>
        </Box>
      </ThemeProvider>
    </Provider>
  )
}

export default App
